// Tuition Service - Financial tuition grid management
// Handles tuition grids, pricing configurations, and sync with pedagogical system

import { apiGet, apiPost, apiPut, apiPatch, apiDelete } from '@/utils/api';
import type {
  TuitionGrid,
  TuitionGridWithRelations,
  TuitionGridQuery,
  PaginatedTuitionGrids,
  DashboardSummary,
  SyncResult,
  CreateTuitionGridData,
  UpdateTuitionGridData,
  BulkCreateTuitionGridData,
  CampusInfo,
  FiliereForTuition,
  TuitionGridSettings,
  CreateTuitionGridSettingsData,
  GroupedFiliereForTuition,
  // Ancillary Fees
  AncillaryFee,
  AncillaryFeeQuery,
  PaginatedAncillaryFees,
  CreateAncillaryFeeData,
  UpdateAncillaryFeeData,
  AssignmentsQuery,
  PaginatedAssignments,
  AssignToStudentsData,
  AssignToFiliereData,
  AssignToCampusData,
  AssignmentResult,
  // Discounts
  DiscountConfig,
  DiscountQuery,
  PaginatedDiscounts,
  CreateDiscountData,
  UpdateDiscountData,
  DiscountAssignmentsQuery,
  PaginatedDiscountAssignments,
  AssignDiscountToStudentsData,
  AssignDiscountToFiliereData,
  AssignDiscountToCampusData,
  DiscountAssignmentResult,
  // Payment Schedules
  PaymentScheduleTemplate,
  PaymentScheduleQuery,
  PaginatedPaymentSchedules,
  CreatePaymentScheduleData,
  UpdatePaymentScheduleData,
  DuplicatePaymentScheduleData,
} from '@/types/tuition.types';

// ==================== Tuition Grids ====================

/**
 * Fetch all tuition grids with pagination and filtering
 */
export async function fetchTuitionGrids(query: TuitionGridQuery): Promise<PaginatedTuitionGrids> {
  const params = new URLSearchParams();

  if (query.filiereId) params.append('filiereId', query.filiereId);
  if (query.campusId) params.append('campusId', query.campusId);
  if (query.includeArchived) params.append('includeArchived', 'true');
  if (query.search) params.append('search', query.search);
  if (query.page) params.append('page', String(query.page));
  params.append('limit', String(query.limit ?? 100));

  return apiGet<PaginatedTuitionGrids>(`/financial/tuition/grids?${params}`);
}

/**
 * Fetch a single tuition grid by ID
 */
export async function fetchTuitionGrid(id: string): Promise<TuitionGridWithRelations> {
  return apiGet<TuitionGridWithRelations>(`/financial/tuition/grids/${id}`);
}

/**
 * Fetch dashboard summary - only filieres with configured prices
 */
export async function fetchDashboardSummary(): Promise<DashboardSummary> {
  return apiGet<DashboardSummary>('/financial/tuition/grids/dashboard-summary');
}

/**
 * Sync filieres from pedagogical system
 * Creates empty grids (0 prices) for new filieres
 * Archives grids for inactive filieres
 */
export async function syncFilieresFromPedagogical(): Promise<SyncResult> {
  return apiPost<SyncResult>('/financial/tuition/grids/sync');
}

/**
 * Create a single tuition grid
 */
export async function createTuitionGrid(data: CreateTuitionGridData): Promise<TuitionGrid> {
  return apiPost<TuitionGrid>('/financial/tuition/grids', data);
}

/**
 * Bulk create tuition grids (for multi-dimension pricing)
 */
export async function createTuitionGridBulk(data: BulkCreateTuitionGridData): Promise<TuitionGrid[]> {
  return apiPost<TuitionGrid[]>('/financial/tuition/grids/bulk', data);
}

/**
 * Update a tuition grid
 */
export async function updateTuitionGrid(
  id: string,
  data: UpdateTuitionGridData
): Promise<TuitionGrid> {
  return apiPut<TuitionGrid>(`/financial/tuition/grids/${id}`, data);
}

/**
 * Archive a tuition grid
 */
export async function archiveTuitionGrid(id: string): Promise<TuitionGrid> {
  return apiPatch<TuitionGrid>(`/financial/tuition/grids/${id}/archive`);
}

/**
 * Restore an archived tuition grid
 */
export async function restoreTuitionGrid(id: string): Promise<TuitionGrid> {
  return apiPatch<TuitionGrid>(`/financial/tuition/grids/${id}/restore`);
}

// ==================== Helper Functions ====================

/**
 * Get campus information for the current tenant
 */
export async function getCampusInfo(): Promise<CampusInfo> {
  return apiGet<CampusInfo>('/filieres/campus/info');
}

/**
 * Fetch active filieres for tuition grid creation
 */
export async function fetchFilieresForTuition(): Promise<FiliereForTuition[]> {
  const data = await apiGet<FiliereForTuition[]>('/filieres?activeOnly=true');

  // Map to simpler format
  return data.map((f: FiliereForTuition) => ({
    id: f.id,
    filiere: f.filiere,
    codeFiliere: f.codeFiliere,
    nombreAnnees: f.nombreAnnees,
    isActive: f.isActive,
  }));
}

// ==================== Currency Formatting ====================

/**
 * Format amount as Moroccan Dirham
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('fr-MA', {
    style: 'currency',
    currency: 'MAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Format niveau as ordinal text
 */
export function formatNiveau(niveau: number): string {
  if (niveau === 1) return '1ere annee';
  return `${niveau}eme annee`;
}

/**
 * Format formation type label
 */
export function formatTypeFormation(type: string): string {
  return type === 'INITIALE' ? 'Formation Initiale' : 'Formation Continue';
}

// ==================== Tuition Grid Settings ====================

/**
 * Fetch tuition grid settings for a tenant (not academic year specific)
 */
export async function fetchTuitionGridSettings(): Promise<TuitionGridSettings | null> {
  try {
    return await apiGet<TuitionGridSettings>('/financial/tuition/settings');
  } catch (error) {
    // Return null if 404 (no settings configured yet)
    if (error instanceof Error && error.message.includes('404')) {
      return null;
    }
    throw error;
  }
}

/**
 * Save tuition grid settings for an academic year
 */
export async function saveTuitionGridSettings(
  data: CreateTuitionGridSettingsData
): Promise<TuitionGridSettings> {
  return apiPost<TuitionGridSettings>('/financial/tuition/settings', data);
}

// ==================== Grouped Filieres ====================

/**
 * Fetch filieres grouped by parent filiere for tuition grid display
 */
export async function fetchFilieresGroupedForTuition(): Promise<GroupedFiliereForTuition[]> {
  return apiGet<GroupedFiliereForTuition[]>('/filieres/grouped?activeOnly=true');
}

/**
 * Get tuition grids grouped by filiere for display
 */
export async function fetchTuitionGridsGrouped(
  campusId?: string,
  includeArchived?: boolean
): Promise<TuitionGridWithRelations[]> {
  const params = new URLSearchParams();
  if (campusId) params.append('campusId', campusId);
  if (includeArchived) params.append('includeArchived', 'true');
  params.append('limit', '500'); // Get all grids for grouping

  const result = await apiGet<PaginatedTuitionGrids>(`/financial/tuition/grids?${params}`);
  return result.data;
}

// ==================== Ancillary Fees ====================

/**
 * Fetch all ancillary fees with pagination and filtering
 */
export async function fetchAncillaryFees(query: AncillaryFeeQuery = {}): Promise<PaginatedAncillaryFees> {
  const params = new URLSearchParams();

  if (query.includeArchived) params.append('includeArchived', 'true');
  if (query.search) params.append('search', query.search);
  if (query.page) params.append('page', String(query.page));
  params.append('limit', String(query.limit ?? 100));

  return apiGet<PaginatedAncillaryFees>(`/financial/tuition/ancillary-fees?${params}`);
}

/**
 * Fetch a single ancillary fee by ID
 */
export async function fetchAncillaryFee(id: string): Promise<AncillaryFee> {
  return apiGet<AncillaryFee>(`/financial/tuition/ancillary-fees/${id}`);
}

/**
 * Create a new ancillary fee
 */
export async function createAncillaryFee(data: CreateAncillaryFeeData): Promise<AncillaryFee> {
  return apiPost<AncillaryFee>('/financial/tuition/ancillary-fees', data);
}

/**
 * Update an ancillary fee
 */
export async function updateAncillaryFee(
  id: string,
  data: UpdateAncillaryFeeData
): Promise<AncillaryFee> {
  return apiPut<AncillaryFee>(`/financial/tuition/ancillary-fees/${id}`, data);
}

/**
 * Archive an ancillary fee (soft delete)
 */
export async function archiveAncillaryFee(id: string): Promise<AncillaryFee> {
  return apiPatch<AncillaryFee>(`/financial/tuition/ancillary-fees/${id}/archive`);
}

/**
 * Restore an archived ancillary fee
 */
export async function restoreAncillaryFee(id: string): Promise<AncillaryFee> {
  return apiPatch<AncillaryFee>(`/financial/tuition/ancillary-fees/${id}/restore`);
}

// ==================== Fee Assignments ====================

/**
 * Get assignments for a specific ancillary fee
 */
export async function fetchFeeAssignments(
  feeId: string,
  query: AssignmentsQuery = {}
): Promise<PaginatedAssignments> {
  const params = new URLSearchParams();

  if (query.includeInactive) params.append('includeInactive', 'true');
  if (query.scope) params.append('scope', query.scope);
  if (query.page) params.append('page', String(query.page));
  params.append('limit', String(query.limit ?? 100));

  return apiGet<PaginatedAssignments>(`/financial/tuition/ancillary-fees/${feeId}/assignments?${params}`);
}

/**
 * Assign an ancillary fee to specific students
 */
export async function assignToStudents(data: AssignToStudentsData): Promise<AssignmentResult> {
  return apiPost<AssignmentResult>('/financial/tuition/ancillary-fees/assign/students', data);
}

/**
 * Assign an ancillary fee to a filiere (optionally with level)
 */
export async function assignToFiliere(data: AssignToFiliereData): Promise<AssignmentResult> {
  return apiPost<AssignmentResult>('/financial/tuition/ancillary-fees/assign/filiere', data);
}

/**
 * Assign an ancillary fee to a campus
 */
export async function assignToCampus(data: AssignToCampusData): Promise<AssignmentResult> {
  return apiPost<AssignmentResult>('/financial/tuition/ancillary-fees/assign/campus', data);
}

/**
 * Remove an assignment (soft delete)
 */
export async function removeAssignment(assignmentId: string): Promise<{ success: boolean; feeItemsRemoved: number }> {
  return apiDelete<{ success: boolean; feeItemsRemoved: number }>(`/financial/tuition/ancillary-fees/assignments/${assignmentId}`);
}

// ==================== Students for Fee Assignment ====================

export interface StudentForFeeAssignment {
  id: string;
  studentCode: string;
  firstName: string;
  lastName: string;
  photoUrl?: string;
}

export interface StudentFeeAssignmentFilters {
  academicYearId: string;
  campusId?: string;
  filiereId?: string;
  niveau?: number;
  groupId?: string;
}

/**
 * Fetch students for fee assignment with optional filters
 */
export async function fetchStudentsForFeeAssignment(
  filters: StudentFeeAssignmentFilters
): Promise<StudentForFeeAssignment[]> {
  const params = new URLSearchParams();
  params.append('academicYearId', filters.academicYearId);
  if (filters.campusId) params.append('campusId', filters.campusId);
  if (filters.filiereId) params.append('filiereId', filters.filiereId);
  if (filters.niveau) params.append('niveau', String(filters.niveau));
  if (filters.groupId) params.append('groupId', filters.groupId);

  return apiGet<StudentForFeeAssignment[]>(`/financial/tuition/students/for-fee-assignment?${params}`);
}

// ==================== Discounts ====================

/**
 * Fetch all discounts with pagination and filtering
 */
export async function fetchDiscounts(query: DiscountQuery = {}): Promise<PaginatedDiscounts> {
  const params = new URLSearchParams();

  if (query.includeArchived) params.append('includeArchived', 'true');
  if (query.search) params.append('search', query.search);
  if (query.type) params.append('type', query.type);
  if (query.page) params.append('page', String(query.page));
  params.append('limit', String(query.limit ?? 100));

  return apiGet<PaginatedDiscounts>(`/financial/tuition/discounts?${params}`);
}

/**
 * Fetch a single discount by ID
 */
export async function fetchDiscount(id: string): Promise<DiscountConfig> {
  return apiGet<DiscountConfig>(`/financial/tuition/discounts/${id}`);
}

/**
 * Create a new discount
 */
export async function createDiscount(data: CreateDiscountData): Promise<DiscountConfig> {
  return apiPost<DiscountConfig>('/financial/tuition/discounts', data);
}

/**
 * Update a discount
 */
export async function updateDiscount(
  id: string,
  data: UpdateDiscountData
): Promise<DiscountConfig> {
  return apiPut<DiscountConfig>(`/financial/tuition/discounts/${id}`, data);
}

/**
 * Archive a discount (soft delete)
 */
export async function archiveDiscount(id: string): Promise<DiscountConfig> {
  return apiPatch<DiscountConfig>(`/financial/tuition/discounts/${id}/archive`);
}

/**
 * Restore an archived discount
 */
export async function restoreDiscount(id: string): Promise<DiscountConfig> {
  return apiPatch<DiscountConfig>(`/financial/tuition/discounts/${id}/restore`);
}

/**
 * Permanently delete a discount (only if never used)
 */
export async function deleteDiscount(id: string): Promise<void> {
  await apiDelete<void>(`/financial/tuition/discounts/${id}`);
}

// ==================== Discount Assignments ====================

/**
 * Get assignments for a specific discount
 */
export async function fetchDiscountAssignments(
  discountId: string,
  query: DiscountAssignmentsQuery = {}
): Promise<PaginatedDiscountAssignments> {
  const params = new URLSearchParams();

  if (query.includeInactive) params.append('includeInactive', 'true');
  if (query.scope) params.append('scope', query.scope);
  if (query.page) params.append('page', String(query.page));
  params.append('limit', String(query.limit ?? 100));

  return apiGet<PaginatedDiscountAssignments>(`/financial/tuition/discounts/${discountId}/assignments?${params}`);
}

/**
 * Assign a discount to specific students
 */
export async function assignDiscountToStudents(
  data: AssignDiscountToStudentsData
): Promise<DiscountAssignmentResult> {
  return apiPost<DiscountAssignmentResult>('/financial/tuition/discounts/assign/students', data);
}

/**
 * Assign a discount to a filiere (optionally with level)
 */
export async function assignDiscountToFiliere(
  data: AssignDiscountToFiliereData
): Promise<DiscountAssignmentResult> {
  return apiPost<DiscountAssignmentResult>('/financial/tuition/discounts/assign/filiere', data);
}

/**
 * Assign a discount to a campus
 */
export async function assignDiscountToCampus(
  data: AssignDiscountToCampusData
): Promise<DiscountAssignmentResult> {
  return apiPost<DiscountAssignmentResult>('/financial/tuition/discounts/assign/campus', data);
}

/**
 * Remove a discount assignment
 */
export async function removeDiscountAssignment(
  assignmentId: string
): Promise<{ success: boolean; discountsRemoved: number }> {
  return apiDelete<{ success: boolean; discountsRemoved: number }>(`/financial/tuition/discounts/assignments/${assignmentId}`);
}

// ==================== PENDING DISCOUNTS APPROVAL ====================

export interface PendingDiscount {
  id: string;
  discountAmount: number;
  status: string;
  notes?: string;
  createdBy?: string;
  createdAt: string;
  discountConfig: {
    id: string;
    name: string;
    code?: string;
    type: string;
  };
  account: {
    id: string;
    studentId: string;
    academicYearId: string;
    student: {
      id: string;
      firstName: string;
      lastName: string;
      studentCode: string;
      filiereInscrit?: string;
      niveauFormation?: number;
    };
  };
  academicYear?: {
    id: string;
    name: string;
  };
  createdByUser?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export interface PendingDiscountsResponse {
  data: PendingDiscount[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Fetch pending discounts awaiting approval
 */
export async function fetchPendingDiscounts(
  query: { page?: number; limit?: number } = {}
): Promise<PendingDiscountsResponse> {
  const params = new URLSearchParams();
  if (query.page) params.append('page', query.page.toString());
  params.append('limit', String(query.limit ?? 100));

  return apiGet<PendingDiscountsResponse>(`/financial/payments/discounts/pending?${params.toString()}`);
}

/**
 * Approve a pending discount
 */
export async function approveDiscount(discountId: string): Promise<PendingDiscount> {
  return apiPost<PendingDiscount>(`/financial/payments/discounts/${discountId}/approve`);
}

/**
 * Reject a pending discount
 */
export async function rejectDiscount(
  discountId: string,
  reason?: string
): Promise<PendingDiscount> {
  return apiPost<PendingDiscount>(`/financial/payments/discounts/${discountId}/reject`, { reason });
}

// ==================== Payment Schedule Templates ====================

/**
 * Fetch all payment schedule templates
 */
export async function fetchPaymentSchedules(
  query: PaymentScheduleQuery = {}
): Promise<PaginatedPaymentSchedules> {
  const params = new URLSearchParams();
  if (query.type) params.append('type', query.type);
  if (query.includeArchived) params.append('includeArchived', 'true');
  if (query.search) params.append('search', query.search);
  if (query.page) params.append('page', String(query.page));
  params.append('limit', String(query.limit ?? 100));

  return apiGet<PaginatedPaymentSchedules>(`/financial/tuition/schedules?${params}`);
}

/**
 * Fetch a single payment schedule template by ID
 */
export async function fetchPaymentSchedule(
  id: string
): Promise<PaymentScheduleTemplate> {
  return apiGet<PaymentScheduleTemplate>(`/financial/tuition/schedules/${id}`);
}

/**
 * Fetch the default payment schedule template
 */
export async function fetchDefaultPaymentSchedule(): Promise<PaymentScheduleTemplate | null> {
  try {
    return await apiGet<PaymentScheduleTemplate>('/financial/tuition/schedules/default');
  } catch (error) {
    // Return null if 404
    if (error instanceof Error && error.message.includes('404')) {
      return null;
    }
    throw error;
  }
}

/**
 * Create a new payment schedule template
 */
export async function createPaymentSchedule(
  data: CreatePaymentScheduleData
): Promise<PaymentScheduleTemplate> {
  return apiPost<PaymentScheduleTemplate>('/financial/tuition/schedules', data);
}

/**
 * Update an existing payment schedule template
 */
export async function updatePaymentSchedule(
  id: string,
  data: UpdatePaymentScheduleData
): Promise<PaymentScheduleTemplate> {
  return apiPut<PaymentScheduleTemplate>(`/financial/tuition/schedules/${id}`, data);
}

/**
 * Set a payment schedule as the default
 */
export async function setDefaultPaymentSchedule(
  id: string
): Promise<PaymentScheduleTemplate> {
  return apiPatch<PaymentScheduleTemplate>(`/financial/tuition/schedules/${id}/set-default`);
}

/**
 * Duplicate a payment schedule template
 */
export async function duplicatePaymentSchedule(
  id: string,
  data: DuplicatePaymentScheduleData
): Promise<PaymentScheduleTemplate> {
  return apiPost<PaymentScheduleTemplate>(`/financial/tuition/schedules/${id}/duplicate`, data);
}

/**
 * Archive a payment schedule template
 */
export async function archivePaymentSchedule(
  id: string
): Promise<PaymentScheduleTemplate> {
  return apiPatch<PaymentScheduleTemplate>(`/financial/tuition/schedules/${id}/archive`);
}

/**
 * Restore an archived payment schedule template
 */
export async function restorePaymentSchedule(
  id: string
): Promise<PaymentScheduleTemplate> {
  return apiPatch<PaymentScheduleTemplate>(`/financial/tuition/schedules/${id}/restore`);
}

/**
 * Delete a payment schedule template (only if not in use)
 */
export async function deletePaymentSchedule(id: string): Promise<void> {
  await apiDelete<void>(`/financial/tuition/schedules/${id}`);
}

// ==================== Payment Schedule Assignment ====================

export interface AssignScheduleToStudentsData {
  templateId: string;
  studentIds: string[];
  academicYearId: string;
  /** Replace the schedule of students who already have one (default: skip them) */
  replaceExisting?: boolean;
}

export interface AlreadyScheduledStudent {
  studentId: string;
  name: string;
  currentScheduleName: string | null;
}

export interface ScheduleAssignmentResult {
  assignedCount: number;
  replacedCount?: number;
  skippedCount: number;
  /** Students skipped because they already have a schedule (for switch confirmation) */
  alreadyScheduled?: AlreadyScheduledStudent[];
  errors?: string[];
}

/**
 * Assign a payment schedule template to multiple students
 */
export async function assignPaymentScheduleToStudents(
  data: AssignScheduleToStudentsData
): Promise<ScheduleAssignmentResult> {
  return apiPost<ScheduleAssignmentResult>('/financial/payments/schedules/assign-bulk', data);
}

export interface ReplaceScheduleData {
  studentId: string;
  templateId: string;
  academicYearId: string;
}

/**
 * Replace an existing payment schedule for a student
 */
export async function replacePaymentSchedule(
  data: ReplaceScheduleData
): Promise<unknown> {
  return apiPost<unknown>('/financial/payments/schedules/replace', data);
}
